import { useState } from "react";
import { Boxes, Pencil, Plus, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, Panel, RowsSkeleton, StatusBadge, Tag } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const STATUS = [["available", "Disponible"], ["beta", "Bêta"], ["disabled", "Désactivé sur la plateforme"]];

export default function Modules() {
  const { me } = useAuth();
  const isSuper = me.user.platform_role === "super_admin";
  const { data, loading, reload } = useFetch("/platform/modules");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(false);
  const names = Object.fromEntries((data || []).map((m) => [m.code, m.name]));
  const baseFields = [
    { key: "name", label: "Nom", required: true },
    { key: "category", label: "Catégorie", required: true },
    { key: "description", label: "Description", type: "textarea", rows: 3 },
  ];
  return (
    <div>
      <PageHeader eyebrow="Catalogue central" title="Modules et fonctionnalités"
        subtitle="Modules disponibles sur la plateforme. Les modules inclus dans une formule et ceux autorisés pour chaque cabinet se gèrent respectivement dans les formules et les fiches cabinets."
        actions={isSuper && <Button onClick={() => setCreate(true)} data-testid="modules-create-button"><Plus className="h-4 w-4 mr-2" />Nouveau module</Button>} />
      <Panel className="overflow-hidden">
        <div className="-m-5 overflow-x-auto">
          {loading ? <RowsSkeleton /> : !data?.length ? <EmptyState icon={Boxes} title="Catalogue vide" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Module</TableHead><TableHead>Identifiant</TableHead><TableHead>Dépendances</TableHead><TableHead>Formules</TableHead><TableHead>Cabinets</TableHead><TableHead>Statut</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {data.map((m) => (
                  <TableRow key={m.code} data-testid={`module-row-${m.code}`}>
                    <TableCell>
                      <p className="font-semibold text-sm flex items-center gap-2">{m.name}{m.is_core && <Tag tone="indigo">Socle</Tag>}{!m.implemented && <Tag tone="gray">En développement</Tag>}</p>
                      <p className="text-xs text-slate-500 max-w-md">{m.description}</p>
                      {m.submodules?.length > 0 && <p className="text-[11px] text-slate-400 mt-1">Sous-modules : {m.submodules.map((s) => s.name).join(", ")}</p>}
                    </TableCell>
                    <TableCell><span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200">{m.code}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{m.dependencies?.length ? <span className="flex items-center gap-1"><Link2 className="h-3 w-3" />{m.dependencies.map((d) => names[d] || d).join(", ")}</span> : "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500">{m.plans.join(", ") || "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{m.tenants_count}</TableCell>
                    <TableCell><StatusBadge status={m.status} prefix="module" /></TableCell>
                    <TableCell>{isSuper && <Button variant="ghost" size="sm" onClick={() => setEdit(m)} data-testid={`module-edit-${m.code}`}><Pencil className="h-4 w-4" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Panel>
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={`Modifier « ${edit?.name} »`} testid="module-edit-dialog"
        description="L'identifiant est stable et ne peut pas être modifié. Désactiver un module sur la plateforme le bloque pour tous les cabinets (les données sont conservées)."
        initial={edit || {}} fields={[...baseFields, { key: "status", label: "Statut", type: "select", options: edit?.is_core ? STATUS.slice(0, 2) : STATUS, required: true }]}
        onSubmit={async (v) => { await api.put(`/platform/modules/${edit.code}`, { name: v.name, category: v.category, description: v.description || "", status: v.status }); toast.success("Module mis à jour"); reload(); }} />
      <FormDialog open={create} onOpenChange={setCreate} title="Nouveau module" testid="module-create-dialog" initial={{ category: "Autre", dependencies: [] }}
        fields={[{ key: "code", label: "Identifiant stable", required: true, mono: true, hint: "Minuscules, chiffres, _ (immuable)" }, ...baseFields,
          { key: "dependencies", label: "Dépendances", type: "checklist", options: (data || []).map((m) => [m.code, m.name]) }]}
        onSubmit={async (v) => { await api.post("/platform/modules", { code: v.code, name: v.name, category: v.category, description: v.description || "", dependencies: v.dependencies || [] }); toast.success("Module créé"); reload(); }} />
    </div>
  );
}
